'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import { returnRefAndGenerateDataTracker } from '@/components/atoms/Button/utils';
import { NextImageProps } from '@/components/atoms/Image';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import { ImageLinkStrapi } from '@/components/atoms/imageLink/ImageLinkStrapi';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { pullDataTrackers } from '@/components/molecules/imageLink/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { HTMLDataTrackerProps } from '@/app/types';

import { isLinkBlock } from './utils';

type BasePopupImageLinkStrapiProps = {
  image: ImageStrapiProps;
};

export type LinkStrapiBlock = BasePopupImageLinkStrapiProps & {
  link: LinkButtonStrapiProps;
  popup?: ImageStrapiProps;
};

type PopupStrapiBlock = BasePopupImageLinkStrapiProps & {
  link?: LinkButtonStrapiProps;
  popup: ImageStrapiProps;
};

export type FooterPopupImageLinkBlock = PopupStrapiBlock | LinkStrapiBlock;

export type FooterPopupImageLinkInner = {
  popup?: {
    showArrow?: boolean;
    position?: 'top' | 'right' | 'bottom' | 'left';
    classNames?: {
      childrenWrapper?: string;
      arrow?: string;
      content?: string;
    };
    contentProps?: PopoverPrimitive.PopoverContentProps;
  };
  popupImage?: Partial<NextImageProps>;
  image?: Partial<NextImageProps>;
  link?: {
    variant?: 'text';
    slim?: boolean;
    'data-tracker-context'?: string;
    classNames?: {
      root?: string;
    };
  } & HTMLDataTrackerProps;
};

export function FooterPopupImageLink({
  block,
  inner,
}: {
  block: FooterPopupImageLinkBlock;
  inner?: FooterPopupImageLinkInner;
}) {
  const { image, popup } = block;

  if (popup) {
    const targetProps = pullDataTrackers(inner?.link || {});
    const showArrow = inner?.popup?.showArrow !== false;
    const position = inner?.popup?.position || 'right';

    return (
      <Popover>
        <PopoverTrigger
          {...targetProps}
          ref={(ref: HTMLElement | null) => {
            if (ref) {
              returnRefAndGenerateDataTracker(ref);
            }
          }}
          className='cursor-pointer'
        >
          <ImageStrapi block={image} inner={inner?.image} />
        </PopoverTrigger>
        <PopoverContent
          side={position}
          sideOffset={inner?.popup?.contentProps?.sideOffset ?? 4}
          className={cn(
            'p-0 border-0 shadow-none',
            inner?.popup?.classNames?.content,
            inner?.popup?.contentProps?.className
          )}
          {...inner?.popup?.contentProps}
        >
          <div
            className={cn(
              'bg-base-white dark:bg-base-dark-white rounded-xl border border-gray-300 p-6',
              inner?.popup?.classNames?.childrenWrapper
            )}
          >
            {showArrow && (
              <PopoverPrimitive.Arrow
                className={cn('fill-gray-300', inner?.popup?.classNames?.arrow)}
              />
            )}
            <ImageStrapi block={popup} inner={inner?.popupImage} />
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  if (isLinkBlock(block)) {
    return (
      <ImageLinkStrapi
        inner={{
          ...inner?.link,
          classNames: {
            ...inner?.link?.classNames,
            image: cn(
              'flex content-center h-6 w-6',
              inner?.image?.classNames?.image
            ),
          },
        }}
        block={block}
      />
    );
  }

  return <ImageStrapi block={image} inner={inner?.image} />;
}
